import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import type { PublicBranding } from '@busbook/shared';
import { applyThemeTokens, buildThemeTokens, resolveColors, webFontHref } from './theme.js';

/** UI primitives shared by the three apps. All colour comes from CSS variables. */

// --- Theme -----------------------------------------------------------------

interface ThemeContextValue {
  branding: PublicBranding;
  prefersDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useBranding(): PublicBranding {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useBranding must be used inside <ThemeProvider>');
  return value.branding;
}

export interface ThemeProviderProps {
  branding: PublicBranding;
  children: ReactNode;
  /** Also set document title, favicon and theme-color (customer app only). */
  applyDocumentIdentity?: boolean;
}

/**
 * Writes the tenant's tokens onto :root and keeps the installed-app chrome in
 * sync with the brand (docs/04 §1.4).
 */
export function ThemeProvider({
  branding,
  children,
  applyDocumentIdentity = false,
}: ThemeProviderProps) {
  const [prefersDark, setPrefersDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    if (branding.themeMode !== 'AUTO' || typeof window === 'undefined') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, [branding.themeMode]);

  const colors = useMemo(() => resolveColors(branding, prefersDark), [branding, prefersDark]);

  useEffect(() => {
    applyThemeTokens(document.documentElement, buildThemeTokens(colors, branding.fontFamily));
    document.documentElement.style.colorScheme =
      branding.themeMode === 'DARK' || (branding.themeMode === 'AUTO' && prefersDark)
        ? 'dark'
        : 'light';
  }, [colors, branding.fontFamily, branding.themeMode, prefersDark]);

  useEffect(() => {
    const href = webFontHref(branding.fontFamily);
    if (!href) return;
    const id = 'busbook-tenant-font';
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;
  }, [branding.fontFamily]);

  useEffect(() => {
    if (!applyDocumentIdentity) return;
    document.title = branding.appName;

    let themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.name = 'theme-color';
      document.head.appendChild(themeColor);
    }
    themeColor.content = colors.primary;

    if (branding.iconUrl) {
      let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!icon) {
        icon = document.createElement('link');
        icon.rel = 'icon';
        document.head.appendChild(icon);
      }
      icon.href = branding.iconUrl;

      // iOS ignores the manifest's icons when adding to the home screen and reads this link
      // instead. Without it an installed app gets a screenshot of the page as its icon --
      // which is the one place a white-label product must not look generic.
      let touchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
      if (!touchIcon) {
        touchIcon = document.createElement('link');
        touchIcon.rel = 'apple-touch-icon';
        document.head.appendChild(touchIcon);
      }
      touchIcon.href = branding.iconUrl;
    }
  }, [applyDocumentIdentity, branding.appName, branding.iconUrl, colors.primary]);

  const value = useMemo(() => ({ branding, prefersDark }), [branding, prefersDark]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// --- Button ----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  block?: boolean;
}

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-[background-color,border-color,opacity] duration-150 disabled:opacity-45 disabled:cursor-not-allowed select-none';

const BUTTON_SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-[0.9375rem]',
  lg: 'h-13 px-6 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  className = '',
  children,
  disabled,
  style,
  ...rest
}: ButtonProps) {
  const variantStyle: Record<ButtonVariant, React.CSSProperties> = {
    primary: { background: 'var(--color-primary)', color: 'var(--color-on-primary)' },
    secondary: {
      background: 'transparent',
      color: 'var(--color-text)',
      border: '1px solid var(--color-line-strong)',
    },
    ghost: { background: 'transparent', color: 'var(--color-primary)' },
    danger: { background: 'var(--signal-cancelled)', color: '#FFFFFF' },
  };

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`${BUTTON_BASE} ${BUTTON_SIZES[size]} ${block ? 'w-full' : ''} ${className}`}
      style={{ ...variantStyle[variant], ...style }}
    >
      {loading && <Spinner size={16} />}
      {children}
    </button>
  );
}

// --- Surfaces --------------------------------------------------------------

export function Card({
  children,
  className = '',
  as: Tag = 'div',
  ...rest
}: { children: ReactNode; className?: string; as?: 'div' | 'section' | 'article' | 'li' } & Record<
  string,
  unknown
>) {
  return (
    <Tag
      {...rest}
      className={`rounded-xl border p-4 ${className}`}
      style={{
        background: 'var(--color-surface)',
        borderColor: 'var(--color-line)',
        ...((rest as { style?: React.CSSProperties }).style ?? {}),
      }}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="eyebrow">{children}</p>;
}

// --- Forms -----------------------------------------------------------------

export interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, hint, error, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && !error && (
        <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {hint}
        </p>
      )}
      {error && (
        <p className="text-xs" style={{ color: 'var(--signal-cancelled)' }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL_CLASS =
  'h-11 w-full rounded-lg border px-3 text-[0.9375rem] outline-none transition-colors placeholder:opacity-45';

const controlStyle: React.CSSProperties = {
  background: 'var(--color-bg)',
  borderColor: 'var(--color-line-strong)',
  color: 'var(--color-text)',
};

export function Input({ className = '', style, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={`${CONTROL_CLASS} ${className}`} style={{ ...controlStyle, ...style }} />;
}

export function Select({
  className = '',
  style,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={`${CONTROL_CLASS} ${className}`} style={{ ...controlStyle, ...style }}>
      {children}
    </select>
  );
}

// --- Feedback --------------------------------------------------------------

export function Spinner({ size = 20, label }: { size?: number; label?: string }) {
  return (
    <span
      role={label ? 'status' : undefined}
      aria-label={label}
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent align-[-2px]"
      style={{ width: size, height: size, opacity: 0.55 }}
    />
  );
}

export type AlertTone = 'info' | 'warning' | 'error' | 'success';

const ALERT_COLOR: Record<AlertTone, string> = {
  info: 'var(--signal-scheduled)',
  warning: 'var(--signal-delayed)',
  error: 'var(--signal-cancelled)',
  success: 'var(--signal-departed)',
};

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className="rounded-lg border-l-[3px] px-3 py-2.5 text-sm"
      style={{
        borderLeftColor: ALERT_COLOR[tone],
        background: 'var(--color-surface)',
        color: 'var(--color-text)',
      }}
    >
      {title && <p className="font-semibold">{title}</p>}
      {children && <div style={{ color: 'var(--color-muted)' }}>{children}</div>}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <p className="text-base font-semibold">{title}</p>
      {description && (
        <p className="max-w-sm text-sm" style={{ color: 'var(--color-muted)' }}>
          {description}
        </p>
      )}
      {action}
    </div>
  );
}

export function Badge({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ background: `color-mix(in srgb, ${color} 14%, transparent)`, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {children}
    </span>
  );
}

// --- Dialogs ---------------------------------------------------------------

/**
 * Modal dialog: dimmed backdrop, centred panel, closes on Esc or backdrop, holds the page still
 * underneath. Colour comes from the same CSS variables as everything else, so a tenant's branding
 * reaches it like it reaches the rest of the app.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  labelledBy = 'busbook-modal-title',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  footer?: ReactNode;
  labelledBy?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className="relative z-10 w-full max-w-md overflow-hidden rounded-xl border shadow-lg"
        style={{ background: 'var(--color-surface)', borderColor: 'var(--color-line)' }}
      >
        <div className="px-5 pt-5">
          <h2 id={labelledBy} className="text-base font-semibold">
            {title}
          </h2>
          {children && <div className="mt-2 text-sm">{children}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5 pt-5">{footer}</div>
      </div>
    </div>
  );
}

/**
 * Asks before an action that cannot be taken back by clicking again.
 *
 * The shared replacement for `window.confirm`, which these apps used for refunds, password resets and
 * paused timetables. That dialog is the browser's, not the product's: it carries the origin instead of
 * the operator's name, ignores the tenant's branding and the reader's language, renders a money amount
 * in whatever the default font is, and collapses a two-paragraph explanation into one run-on line. On
 * a screen where somebody is about to move money back to a passenger's card, looking like the product
 * is not decoration.
 *
 * `body` may be several lines; each is its own paragraph, which is what the `\n\n` in the old messages
 * was reaching for.
 *
 * Cancel is focused rather than Confirm, so a stray Enter does not perform the action the dialog was
 * added to guard.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = true,
  pending = false,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  /** One string, or several shown as separate paragraphs. */
  body?: string | string[];
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const paragraphs = body === undefined ? [] : Array.isArray(body) ? body : [body];
  const close = () => {
    if (!pending) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      footer={
        <>
          {/* Modal unmounts while closed, so autoFocus lands on every open, not just the first. */}
          <Button autoFocus variant="secondary" onClick={close} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {paragraphs.map((paragraph, i) => (
        <p key={i} className={i === 0 ? '' : 'mt-2'} style={{ color: 'var(--color-muted)' }}>
          {paragraph}
        </p>
      ))}
      {error && (
        <p role="alert" className="mt-3 text-sm font-medium" style={{ color: 'var(--signal-cancelled)' }}>
          {error}
        </p>
      )}
    </Modal>
  );
}

export interface ConfirmRequest {
  title: string;
  body?: string | string[];
  confirmLabel: string;
  cancelLabel?: string;
  /** Defaults to true. Set false for a confirmation that is merely significant, not destructive. */
  destructive?: boolean;
}

/**
 * `window.confirm`, as a dialog that belongs to this product.
 *
 * Deliberately shaped to be awaited the same way the native call was read, so a screen that had
 *
 *   if (!window.confirm('Refund …?')) return;
 *
 * becomes
 *
 *   if (!(await ask({ title: 'Refund …?', confirmLabel: 'Refund' }))) return;
 *
 * and everything after it — the busy flag, the try/catch, the error line — is untouched. Rewriting
 * eleven flows by hand to thread dialog state through each one would have risked eleven new bugs in
 * screens that refund passengers and reset staff passwords, to change how a question is asked.
 *
 * Render `dialog` anywhere in the component; it draws nothing until something is being asked.
 */
export function useConfirm(): { ask: (request: ConfirmRequest) => Promise<boolean>; dialog: ReactNode } {
  const [request, setRequest] = useState<(ConfirmRequest & { settle: (ok: boolean) => void }) | null>(null);

  const ask = useCallback(
    (next: ConfirmRequest) => new Promise<boolean>((resolve) => setRequest({ ...next, settle: resolve })),
    [],
  );

  // Answering always clears the request, so an unmount mid-question cannot leave a promise pending.
  const settle = (ok: boolean) => {
    setRequest((current) => {
      current?.settle(ok);
      return null;
    });
  };

  const dialog = (
    <ConfirmDialog
      open={request !== null}
      title={request?.title ?? ''}
      body={request?.body}
      confirmLabel={request?.confirmLabel ?? ''}
      cancelLabel={request?.cancelLabel}
      destructive={request?.destructive ?? true}
      onConfirm={() => settle(true)}
      onClose={() => settle(false)}
    />
  );

  return { ask, dialog };
}

export interface PromptRequest {
  title: string;
  body?: string | string[];
  /** Label above the input, e.g. "Reason". */
  label: string;
  placeholder?: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** Defaults to true: an empty answer keeps the Confirm button disabled. */
  required?: boolean;
}

/**
 * `window.prompt`, as a dialog that belongs to this product — the sibling of {@link useConfirm} for
 * an action that needs a word of explanation before it happens.
 *
 * Resolves to the text entered, or null if the question was dismissed, so
 *
 *   const reason = window.prompt('Suspend …? Reason:');
 *   if (reason) …
 *
 * keeps its shape. The native prompt could not say what suspending does and ask for the reason as
 * separate things — it had one paragraph for the explanation, the label and the field, which is why
 * the message it replaced ended in a bare "Reason:".
 */
export function usePrompt(): { ask: (request: PromptRequest) => Promise<string | null>; dialog: ReactNode } {
  const [request, setRequest] = useState<(PromptRequest & { settle: (value: string | null) => void }) | null>(null);
  const [value, setValue] = useState('');

  const ask = useCallback(
    (next: PromptRequest) =>
      new Promise<string | null>((resolve) => {
        setValue('');
        setRequest({ ...next, settle: resolve });
      }),
    [],
  );

  const settle = (answer: string | null) => {
    setRequest((current) => {
      current?.settle(answer);
      return null;
    });
  };

  const paragraphs = request?.body === undefined ? [] : Array.isArray(request.body) ? request.body : [request.body];
  const required = request?.required ?? true;
  const answered = value.trim() !== '';

  const dialog = (
    <Modal
      open={request !== null}
      onClose={() => settle(null)}
      title={request?.title ?? ''}
      labelledBy="busbook-prompt-title"
      footer={
        <>
          <Button variant="secondary" onClick={() => settle(null)}>
            {request?.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            variant={request?.destructive ?? true ? 'danger' : 'primary'}
            disabled={required && !answered}
            onClick={() => settle(value.trim())}
          >
            {request?.confirmLabel ?? ''}
          </Button>
        </>
      }
    >
      {paragraphs.map((paragraph, i) => (
        <p key={i} className={i === 0 ? '' : 'mt-2'} style={{ color: 'var(--color-muted)' }}>
          {paragraph}
        </p>
      ))}
      <div className={paragraphs.length ? 'mt-3' : ''}>
        <Field label={request?.label ?? ''} htmlFor="busbook-prompt-input">
          <Input
            id="busbook-prompt-input"
            autoFocus
            value={value}
            placeholder={request?.placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (!required || answered)) settle(value.trim());
            }}
          />
        </Field>
      </div>
    </Modal>
  );

  return { ask, dialog };
}
